/**
 * RelatedResources — internal-linking block for the end of every blog post.
 *
 * Why this exists (SEO): the ranking Redemittel posts previously had ZERO internal
 * links, so they passed no link equity to the commercial/transactional pages
 * (which sit at pos 30–50 in Search Console) and built no B1↔B2↔C1 topic cluster.
 * This block funnels equity to:
 *   1. the free no-signup mock exam (/try)             — transactional entry point
 *   2. the exam prep landing page (telc-b1/b2 only)    — commercial page
 *   3. this exam's own /mock-exams/{slug} page         — the per-exam SEO pages
 *      (shipped 2026-07-20) have no internal links from ranking content otherwise
 *   4. the counterpart posts (redemittel ↔ exam-guide ↔ sprechen samples) — intent cluster
 *   5. the sibling levels of the same provider         — B1↔B2↔C1 level cluster
 *
 * All links are real, crawlable <a href> (react-router <Link>) with the current
 * /:lang prefix, so they appear in the prerendered HTML the crawlers read.
 *
 * Labels go through t() with English defaults; the German strings (blog.related.*) serve
 * the posts in src/data/germanBlog.js.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { useLangParam } from '../../hooks/useLocalizedNavigate';

const PROVIDER_LABEL = { telc: 'TELC', goethe: 'Goethe', osd: 'ÖSD' };
const LEVELS = ['b1', 'b2', 'c1'];

// Landing pages currently exist only for these provider-level pairs; the other
// exams link straight to their own /mock-exams/{slug} page instead.
const LANDING_PAGES = new Set(['telc-b1', 'telc-b2']);

function ResourceLink({ to, title, note, primary = false }) {
  return (
    <Link
      to={to}
      className={`
        group flex items-center justify-between gap-4 min-h-[48px] px-5 py-4
        rounded-2xl border-2 transition-all duration-300
        active:scale-[0.98] focus:outline-none focus-visible:ring-2 focus-visible:ring-black/40
        ${primary
          ? 'bg-black text-white border-black hover:shadow-xl hover:scale-[1.01]'
          : 'bg-white text-black border-black/10 hover:border-black/40 hover:shadow-lg hover:scale-[1.01]'
        }
      `}
    >
      <span className="flex-1">
        <span className="font-space font-bold text-base md:text-lg leading-tight block">
          {title}
        </span>
        {note && (
          <span className={`text-sm ${primary ? 'text-white/70' : 'text-black/50'}`}>
            {note}
          </span>
        )}
      </span>
      <ArrowRight
        className={`
          w-5 h-5 flex-shrink-0 transition-transform duration-300 group-hover:translate-x-1
          ${primary ? 'text-white' : 'text-black/60'}
        `}
      />
    </Link>
  );
}

// Post slug suffix per post type in the exam cluster; the English link labels are the
// t() defaults below (blog.related.type.<type>.label / .counterpart).
const POST_TYPES = {
  redemittel: { suffix: 'redemittel-schreiben', label: 'Redemittel', counterpartLabel: 'Redemittel (free PDF)' },
  'exam-guide': { suffix: 'exam-guide', label: 'exam guide', counterpartLabel: 'exam guide & strategy' },
  sprechen: { suffix: 'sprechen-sample-exams', label: 'Sprechen sample exams', counterpartLabel: 'Sprechen: 3 sample exams with model dialogues' },
};

/**
 * @param {{ provider: 'telc'|'goethe'|'osd', level: 'b1'|'b2'|'c1', variant: 'redemittel'|'exam-guide'|'sprechen' }} props
 */
const RelatedResources = ({ provider, level, variant }) => {
  const lang = useLangParam();
  const { t } = useTranslation();
  const providerLabel = PROVIDER_LABEL[provider] || provider.toUpperCase();
  const levelLabel = level.toUpperCase();

  const blogPath = (p, l, v) => `/${lang}/blog/${p}-${l}-${POST_TYPES[v].suffix}`;

  const examSlug = `${provider}-${level}`;
  const hasLandingPage = LANDING_PAGES.has(examSlug);
  const mockExamPath = `/${lang}/mock-exams/${examSlug}`;
  const practicePath = hasLandingPage ? `/${lang}/${examSlug}` : mockExamPath;

  // Every post links to the other two post types of the same exam.
  const counterparts = Object.keys(POST_TYPES).filter((v) => v !== variant);

  const siblingLevels = LEVELS.filter((l) => l !== level);

  return (
    <section
      aria-label={t('blog.related.aria', { provider: providerLabel, level: levelLabel, defaultValue: 'More {{provider}} {{level}} preparation resources' })}
      className="mt-14 pt-8 border-t-2 border-black/10"
    >
      <h2 className="font-space text-xl md:text-2xl font-bold text-black mb-2">
        {t('blog.related.heading', { provider: providerLabel, level: levelLabel, defaultValue: 'Continue your {{provider}} {{level}} preparation' })}
      </h2>
      <p className="text-black/60 text-base mb-6">
        {t('blog.related.intro', { provider: providerLabel, level: levelLabel, defaultValue: 'Turn these phrases into a real score: practise on authentic {{provider}} {{level}} exams, then explore the rest of the level.' })}
      </p>

      <div className="grid gap-3 sm:grid-cols-2">
        <ResourceLink
          primary
          to={`/${lang}/try`}
          title={t('blog.related.try.title', { provider: providerLabel, level: levelLabel, defaultValue: 'Take a free {{provider}} {{level}} mock exam' })}
          note={t('blog.related.try.note', { defaultValue: 'Full exam format · no sign-up needed' })}
        />
        <ResourceLink
          to={practicePath}
          title={t('blog.related.prep.title', { provider: providerLabel, level: levelLabel, defaultValue: '{{provider}} {{level}} exam prep & mock exams' })}
          note={t('blog.related.prep.note', { defaultValue: 'Timed sections · AI writing feedback' })}
        />
        {hasLandingPage && (
          <ResourceLink
            to={mockExamPath}
            title={t('blog.related.format.title', { provider: providerLabel, level: levelLabel, defaultValue: '{{provider}} {{level}} mock exam: format & timing' })}
            note={t('blog.related.format.note', { defaultValue: 'All sections · complete practice papers' })}
          />
        )}
        {counterparts.map((v) => (
          <ResourceLink
            key={v}
            to={blogPath(provider, level, v)}
            title={`${providerLabel} ${levelLabel} ${t(`blog.related.type.${v}.counterpart`, { defaultValue: POST_TYPES[v].counterpartLabel })}`}
          />
        ))}
        {siblingLevels.map((l) => (
          <ResourceLink
            key={l}
            to={blogPath(provider, l, variant)}
            title={`${providerLabel} ${l.toUpperCase()} ${t(`blog.related.type.${variant}.label`, { defaultValue: POST_TYPES[variant].label })}`}
          />
        ))}
      </div>
    </section>
  );
};

export default RelatedResources;
